import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TgkMessageModel } from "../../../components/generic/chat/types";
import { Tag } from "../../../components/interfaces/Tag";
import { Rule, RuleType } from "../../../components/interfaces/Rule";
import { remapScriptTagIds } from "../../../utils/helpers/TagMergeHelper";

export interface PendingActionData {
  tags: Tag[];
  rules: Rule[];
}

export interface StagedEdit<T> {
  targetId: string;
  patch: T;
  capturedVersion: T;
}

interface AskAiChatState {
  messages: TgkMessageModel[];
  pendingActionData: PendingActionData | null;
  stagedTags: Tag[];
  stagedRules: Rule[];
  launchRuleIds: string[];
  stagedTagEdits: StagedEdit<Tag>[];
  stagedRuleEdits: StagedEdit<Rule>[];
}

const initialState: AskAiChatState = {
  messages: [],
  pendingActionData: null,
  stagedTags: [],
  stagedRules: [],
  launchRuleIds: [],
  stagedTagEdits: [],
  stagedRuleEdits: [],
};

const askAiChatSlice = createSlice({
  name: "askAiChat",
  initialState,
  reducers: {
    addMessage(state, action: PayloadAction<TgkMessageModel>) {
      state.messages.push(action.payload);
    },
    setMessages(state, action: PayloadAction<TgkMessageModel[]>) {
      state.messages = action.payload;
    },
    resetMessages(state) {
      state.messages = [];
      state.pendingActionData = null;
      state.stagedTags = [];
      state.stagedRules = [];
      state.launchRuleIds = [];
      state.stagedTagEdits = [];
      state.stagedRuleEdits = [];
    },
    setPendingActionData(state, action: PayloadAction<PendingActionData>) {
      state.pendingActionData = action.payload;
    },
    clearPendingActionData(state) {
      state.pendingActionData = null;
    },
    setStagedTags(state, action: PayloadAction<Tag[]>) {
      state.stagedTags = action.payload;
    },
    setStagedRules(state, action: PayloadAction<Rule[]>) {
      state.stagedRules = action.payload;
    },
    clearStagingArea(state) {
      state.stagedTags = [];
      state.stagedRules = [];
      state.stagedTagEdits = [];
      state.stagedRuleEdits = [];
    },
    upsertStagedTagEdit(state, action: PayloadAction<{ targetId: string; patch: Tag; capturedVersion: Tag }>) {
      const { targetId, patch, capturedVersion } = action.payload;
      const existing = state.stagedTagEdits.find((e) => e.targetId === targetId);
      if (existing) {
        existing.patch = patch;
      } else {
        state.stagedTagEdits.push({ targetId, patch, capturedVersion });
      }
    },
    removeStagedTagEdit(state, action: PayloadAction<string>) {
      state.stagedTagEdits = state.stagedTagEdits.filter((e) => e.targetId !== action.payload);
    },
    upsertStagedRuleEdit(state, action: PayloadAction<{ targetId: string; patch: Rule; capturedVersion: Rule }>) {
      const { targetId, patch, capturedVersion } = action.payload;
      const existing = state.stagedRuleEdits.find((e) => e.targetId === targetId);
      if (existing) {
        existing.patch = patch;
      } else {
        state.stagedRuleEdits.push({ targetId, patch, capturedVersion });
      }
    },
    removeStagedRuleEdit(state, action: PayloadAction<string>) {
      state.stagedRuleEdits = state.stagedRuleEdits.filter((e) => e.targetId !== action.payload);
    },
    applyRemapToStagedRuleEdits(state, action: PayloadAction<Record<string, string> | Map<string, string>>) {
      const remap = action.payload;
      const lookup = (id: string): string => {
        if (remap instanceof Map) {
          return remap.get(id) ?? id;
        }
        return remap[id] ?? id;
      };
      for (const entry of state.stagedRuleEdits) {
        if (entry.patch.tagIds) {
          entry.patch.tagIds = entry.patch.tagIds.map(lookup);
        }
        if (entry.patch.type === RuleType.CUSTOM && entry.patch.script) {
          entry.patch.script = remapScriptTagIds(entry.patch.script, remap);
        }
      }
    },
    clearStagedTagEdits(state) {
      state.stagedTagEdits = [];
    },
    clearStagedRuleEdits(state) {
      state.stagedRuleEdits = [];
    },
    setLaunchRuleIds(state, action: PayloadAction<string[]>) {
      state.launchRuleIds = action.payload;
    },
  },
});

export const {
  addMessage,
  setMessages,
  resetMessages,
  setPendingActionData,
  clearPendingActionData,
  setStagedTags,
  setStagedRules,
  clearStagingArea,
  setLaunchRuleIds,
  upsertStagedTagEdit,
  removeStagedTagEdit,
  upsertStagedRuleEdit,
  removeStagedRuleEdit,
  applyRemapToStagedRuleEdits,
  clearStagedTagEdits,
  clearStagedRuleEdits,
} = askAiChatSlice.actions;
export default askAiChatSlice.reducer;
